import { Component, OnInit } from '@angular/core';
import {
  ExchangeRateService,
  ExchangeRateResponseDTO,
  ExchangeRateHistoryResponse
} from '../../../services/exchange-rate.service';
import { ToastService } from '../../../services/toast.service';
import { LoadingService } from '../../../services/loading.service';
import { ApexAxisChartSeries, ApexChart, ApexXAxis, ApexYAxis, ApexDataLabels, ApexStroke, ApexMarkers } from 'ng-apexcharts';

interface FilterOptions {
  fromCurrency: string;
  toCurrency: string;
  dateFrom: string;
  dateTo: string;
}

@Component({
  selector: 'app-exchange-rate-history',
  templateUrl: './exchange-rate-history.component.html',
  styleUrls: ['./exchange-rate-history.component.scss']
})
export class ExchangeRateHistoryComponent implements OnInit {

  exchangeRates: ExchangeRateResponseDTO[] = [];
  filteredRates: ExchangeRateResponseDTO[] = [];
  isLoading = false;

  // Paginación
  currentPage = 1;
  pageSize = 20;
  totalRecords = 0;
  totalPages = 0;

  // Filtros
  filters: FilterOptions = {
    fromCurrency: '',
    toCurrency: '',
    dateFrom: '',
    dateTo: ''
  };

  // Opciones de moneda (se podrían cargar desde un servicio de monedas)
  currencies = [
    { code: 'USD', name: 'Dólar Estadounidense' },
    { code: 'EUR', name: 'Euro' },
    { code: 'PEN', name: 'Sol Peruano' },
    { code: 'COP', name: 'Peso Colombiano' },
    { code: 'BRL', name: 'Real Brasileño' },
    { code: 'MXN', name: 'Peso Mexicano' },
    { code: 'GBP', name: 'Libra Esterlina' },
    { code: 'JPY', name: 'Yen Japonés' }
  ];

  // Opciones de paginación
  pageSizeOptions = [10, 20, 50, 100];

  // KPIs
  highestRate = 0;
  lowestRate = 0;
  averageRate = 0;
  totalVariation = 0;
  lastUpdate = '';

  // Configuración de gráficos
  chartSeries: ApexAxisChartSeries = [];
  chartOptions: ApexChart = {
    type: 'line',
    height: 350,
    toolbar: { show: true },
    zoom: { enabled: true }
  };
  chartXAxis: ApexXAxis = {
    type: 'datetime',
    title: { text: 'Fecha' }
  };
  chartYAxis: ApexYAxis = {
    title: { text: 'Tasa de Cambio' },
    labels: {
      formatter: (value: number) => value.toFixed(4)
    }
  };
  chartDataLabels: ApexDataLabels = {
    enabled: false
  };
  chartStroke: ApexStroke = {
    curve: 'smooth',
    width: 3
  };
  chartMarkers: ApexMarkers = {
    size: 4,
    hover: { size: 6 }
  };
  chartColors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

  constructor(
    private exchangeRateService: ExchangeRateService,
    private toastService: ToastService,
    private loadingService: LoadingService
  ) { }

  ngOnInit(): void {
    this.loadExchangeRateHistory();
  }

  /**
   * Carga el histórico de tasas de cambio
   */
  loadExchangeRateHistory(): void {
    this.isLoading = true;
    this.loadingService.show();

    const fromCurrency = this.filters.fromCurrency || undefined;
    const toCurrency = this.filters.toCurrency || undefined;

    this.exchangeRateService.getExchangeRateHistory(
      fromCurrency,
      toCurrency,
      this.currentPage,
      this.pageSize,
      this.filters.dateFrom || undefined,
      this.filters.dateTo || undefined
    ).subscribe({
      next: (response: ExchangeRateHistoryResponse) => {
        if (response.isSuccess) {
          this.exchangeRates = response.data;
          this.filteredRates = [...this.exchangeRates];

          if (response.pagination) {
            this.currentPage = response.pagination.page;
            this.pageSize = response.pagination.pageSize;
            this.totalRecords = response.pagination.totalRecords;
            this.totalPages = response.pagination.totalPages;
          }

          // Actualizar estadísticas y gráficos
          this.updateStatistics();
          this.updateChartData();
        } else {
          this.toastService.showToast('error', 'Error al cargar el histórico de tasas');
        }
        this.isLoading = false;
        this.loadingService.hide();
      },
      error: (error) => {
        console.error('Error loading exchange rate history:', error);
        this.toastService.showToast('error', 'Error al cargar el histórico de tasas de cambio');
        this.isLoading = false;
        this.loadingService.hide();
      }
    });
  }

  /**
   * Aplica los filtros de búsqueda
   */
  applyFilters(): void {
    this.currentPage = 1;
    this.loadExchangeRateHistory();
  }

  /**
   * Limpia todos los filtros
   */
  clearFilters(): void {
    this.filters = {
      fromCurrency: '',
      toCurrency: '',
      dateFrom: '',
      dateTo: ''
    };
    this.currentPage = 1;
    this.loadExchangeRateHistory();
  }

  /**
   * Cambia la página actual
   */
  onPageChange(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.loadExchangeRateHistory();
    }
  }

  /**
   * Cambia el tamaño de página
   */
  onPageSizeChange(newPageSize: number): void {
    this.pageSize = newPageSize;
    this.currentPage = 1;
    this.loadExchangeRateHistory();
  }

  /**
   * Obtiene las páginas para mostrar en la paginación
   */
  getVisiblePages(): number[] {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (let i = Math.max(2, this.currentPage - delta);
      i <= Math.min(this.totalPages - 1, this.currentPage + delta);
      i++) {
      range.push(i);
    }

    if (this.currentPage - delta > 2) {
      rangeWithDots.push(1, -1); // -1 representa "..."
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (this.currentPage + delta < this.totalPages - 1) {
      rangeWithDots.push(-1, this.totalPages); // -1 representa "..."
    } else if (this.totalPages > 1) {
      rangeWithDots.push(this.totalPages);
    }

    return rangeWithDots;
  }

  /**
   * Actualiza manualmente las tasas
   */
  refreshRates(): void {
    this.loadingService.show();
    this.exchangeRateService.updateRates().subscribe({
      next: (response) => {
        if (response.isSuccess) {
          this.toastService.showToast('success', 'Tasas actualizadas exitosamente');
          this.loadExchangeRateHistory(); // Recargar datos
        } else {
          this.toastService.showToast('error', response.message || 'Error al actualizar las tasas');
        }
        this.loadingService.hide();
      },
      error: (error) => {
        console.error('Error updating rates:', error);
        this.toastService.showToast('error', 'Error al actualizar las tasas de cambio');
        this.loadingService.hide();
      }
    });
  }

  /**
   * Actualiza las estadísticas basadas en los datos actuales
   */
  private updateStatistics(): void {
    if (!this.exchangeRates || this.exchangeRates.length === 0) {
      this.highestRate = 0;
      this.lowestRate = 0;
      this.averageRate = 0;
      this.totalVariation = 0;
      this.lastUpdate = '';
      return;
    }

    const rates = this.exchangeRates.map(r => r.rate);
    this.highestRate = Math.max(...rates);
    this.lowestRate = Math.min(...rates);
    this.averageRate = rates.reduce((sum, rate) => sum + rate, 0) / rates.length;
    this.totalVariation = ((this.highestRate - this.lowestRate) / this.lowestRate) * 100;

    // Fecha de última actualización
    const sortedByDate = [...this.exchangeRates].sort((a, b) =>
      new Date(b.retrievedAt).getTime() - new Date(a.retrievedAt).getTime()
    );
    this.lastUpdate = sortedByDate[0]?.retrievedAt || '';
  }

  /**
   * Actualiza los datos del gráfico
   */
  private updateChartData(): void {
    if (!this.exchangeRates || this.exchangeRates.length === 0) {
      this.chartSeries = [];
      return;
    }

    // Agrupar por par de monedas
    const currencyPairs = new Map<string, Array<{ date: string, rate: number }>>();

    this.exchangeRates.forEach(rate => {
      const pairKey = `${rate.fromCurrency.code}/${rate.toCurrency.code}`;
      if (!currencyPairs.has(pairKey)) {
        currencyPairs.set(pairKey, []);
      }
      currencyPairs.get(pairKey)!.push({
        date: rate.effectiveDate,
        rate: rate.rate
      });
    });

    // Crear series para el gráfico
    this.chartSeries = Array.from(currencyPairs.entries()).map(([pairKey, data], index) => ({
      name: pairKey,
      data: data
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
        .map(d => ({
          x: new Date(d.date).getTime(),
          y: d.rate
        }))
    }));
  }

  /**
   * Formatea la fecha para mostrar
   */
  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  /**
   * Formatea la fecha efectiva
   */
  formatEffectiveDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  }

  /**
   * Formatea el número de la tasa
   */
  formatRate(rate: number): string {
    return rate.toLocaleString('es-ES', {
      minimumFractionDigits: 4,
      maximumFractionDigits: 6
    });
  }

  /**
   * Obtiene el nombre de la moneda por código
   */
  getCurrencyName(code: string): string {
    const currency = this.currencies.find(c => c.code === code);
    return currency ? currency.name : code;
  }

  /**
   * Obtiene el color del badge según la fuente
   */
  getSourceBadgeColor(source: string): string {
    switch (source.toLowerCase()) {
      case 'exchangerateapi':
      case 'currencybeacon':
        return 'bg-green-100 text-green-800';
      case 'manual':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }

  /**
   * Verifica si hay datos para mostrar
   */
  get hasData(): boolean {
    return this.exchangeRates.length > 0;
  }

  /**
   * Obtiene el texto del rango de registros mostrados
   */
  get recordsRangeText(): string {
    if (this.totalRecords === 0) return 'No hay registros';

    const start = (this.currentPage - 1) * this.pageSize + 1;
    const end = Math.min(this.currentPage * this.pageSize, this.totalRecords);

    return `Mostrando ${start} a ${end} de ${this.totalRecords} registros`;
  }
}
